import * as Yup from 'yup';
import { isEqual, isValid } from 'date-fns';

import { Test, type TestProps } from './test.types';
import type { UserProps } from './user.types';
import { Request, type PaymentStatus, type Status } from './request.types';

/*
	Define attrs that need validations in yup. i.e add/edit a bulk request
	Otherwise, define them as ts types - see below
	
	Yup.InferType<typeof addSchema> & {
		amount?: number;
	};	
*/
const schema = Yup.object().shape({
	id: Yup.number().defined().optional(),
	// for users to choose a preferred date when creating a request
	prefAssessmentDates: Yup.array()
		.of(Yup.date().nullable().defined())
		.max(3)
		.required()
		.default(() => [Request.calculateNextValidDate(new Date()), null])
		.test('no-duplicates', 'Preferred assessment dates must be unique (including time)', (dates) => {
			if (!Array.isArray(dates)) return false;

			const filtered = dates.filter((d): d is Date => d instanceof Date);

			const uniqueDates = filtered.reduce((prev: Date[], next) => {
				const alreadyExists = prev.some((d) => {
					return isEqual(d, next);
				});
				if (!alreadyExists) prev.push(next);
				return prev;
			}, []);

			// if false there is duplicated dates
			return uniqueDates.length === filtered.length;
		}),
	tests: Yup.array(Test.schema)
		.default([] as TestProps[])
		.min(1)
		.required()
		.test('must-select-three-test', 'You must select at least 3 tests to continue', (tests) => {
			const count = tests
				.filter((t) => !Test.isFlatTravelFee(t.id))
				.reduce((sum, t) => sum + Number(t.quantity || 0), 0);

			return count >= 3;
		}),
	location: Yup.string().default('').trim().required('Address is required'),
	tncAccepted: Yup.boolean()
		.oneOf([true], 'You must accept the terms and conditions.')
		.default(false)
		.required('You must accept the terms and conditions.'),
});

const addSchema = schema.pick(['tests', 'location', 'tncAccepted', 'prefAssessmentDates']);

export type AddBulkRequestProps = Yup.InferType<typeof addSchema> & {
	amount?: number;
};

const viewSchema = schema.pick(['id', 'tests', 'location', 'prefAssessmentDates']).shape({
	id: Yup.number().defined().required(),
	status: Yup.mixed<Status>().oneOf(Object.values(Request.status)).required(),
});

const editSchema = schema.pick(['id', 'tests', 'location']).shape({
	id: Yup.number().defined().required(),
	status: Yup.mixed<Status>().oneOf(Object.values(Request.status)).required(),
});

export type BulkRequestProps = Yup.InferType<typeof viewSchema> & {
	user?: UserProps;
	createdAt?: Date;
	updatedAt?: Date;
	paymentStatus?: PaymentStatus;
	adminNote?: string;
	accountNote?: string;
	assessmentDates?: (Date | null)[];
	isArchived?: boolean;
	amount?: number;
};

const isAssessmentDatesSet = (dates: BulkRequestProps['assessmentDates'] = []) => {
	return dates.some((date) => {
		return isValid(new Date(date ?? ''));
	});
};

const isCancelled = (status: string | undefined) => {
	return status === 'CANCELLED';
};

const testsSchema = schema.pick(['tests']);
const preferredAssessmentDateSchema = schema.pick(['prefAssessmentDates']);
const locationSchema = schema.pick(['location']);
const reviewSchema = schema.pick(['tncAccepted']);

export const BulkRequest = {
	addSchema,
	editSchema,
	testsSchema,
	preferredAssessmentDateSchema,
	locationSchema,
	reviewSchema,

	isAssessmentDatesSet,
	isCancelled,
};
